import React, { useState, useEffect } from "react";
import { Spin, message, Select, Empty } from "antd";
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import moment from 'moment';
import axios from 'axios';
import { useKeycloak } from '@react-keycloak/web';
import { normalizeEstimate, formatGb, formatUsd, formatCount } from '../../ducks/estimateBilling';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CHART_TYPES = {
  AREA: 'area',
  BAR: 'bar',
  LINE: 'line',
  PIE: 'pie'
};

const chartOptions = [
  { value: CHART_TYPES.AREA, label: 'Area' },
  { value: CHART_TYPES.BAR, label: 'Bars' },
  { value: CHART_TYPES.LINE, label: 'Line' },
  { value: CHART_TYPES.PIE, label: 'Pie' }
];

// Palette in oklch, matching the Billing Usage design file.
const C = {
  violet: 'oklch(0.55 0.19 285)',
  violetDeep: 'oklch(0.45 0.19 285)',
  blue: 'oklch(0.72 0.14 245)',
  // Bandwidth is a different unit on its own axis, so it gets its own hue.
  teal: 'oklch(0.66 0.12 175)',
  ink: 'oklch(0.22 0.02 285)',
  ink25: 'oklch(0.25 0.02 285)',
  body: 'oklch(0.45 0.02 285)',
  muted: 'oklch(0.5 0.02 285)',
  muted55: 'oklch(0.55 0.02 285)',
  muted57: 'oklch(0.57 0.02 285)',
  muted6: 'oklch(0.6 0.02 285)',
  line: 'oklch(0.93 0.008 285)',
  lineSoft: 'oklch(0.95 0.008 285)',
  lineStrong: 'oklch(0.9 0.02 285)',
  grid: 'oklch(0.92 0.008 285)',
  wash: 'oklch(0.972 0.012 285)',
  chip: 'oklch(0.96 0.02 285)',
  white: 'oklch(1 0 0)',
  shadow: '0 1px 2px oklch(0.4 0.05 285 / 0.05)'
};

const SANS = "'Plus Jakarta Sans', system-ui, -apple-system, 'SF Pro Display', sans-serif";
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const card = {
  background: C.white,
  border: `1px solid ${C.line}`,
  borderRadius: 18,
  boxShadow: C.shadow,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0
};

const label = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: C.muted55
};

const figure = {
  fontSize: 'clamp(22px, 2.4vw, 28px)',
  fontWeight: 800,
  letterSpacing: '-0.03em',
  lineHeight: 1
};

const SCOPED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

.eb-root { font-family: ${SANS}; color: ${C.ink}; }
/* The wrapper class pins a fixed height in _dashboard.scss; this section grows
   with its content, so that height is released here. */
.eb-root.col-design3 { height: auto; padding: 0; }

.eb-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  grid-template-rows: auto 1fr;
  gap: 16px;
  align-items: stretch;
}
.eb-aside { grid-column: 2; grid-row: 1 / span 2; }
@media (max-width: 1200px) {
  .eb-grid { grid-template-columns: minmax(0, 1fr); grid-template-rows: auto; }
  .eb-aside { grid-column: 1; grid-row: auto; }
}

/* Segmented chart picker */
.eb-seg {
  display: flex;
  background: ${C.white};
  border: 1px solid ${C.line};
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
  box-shadow: 0 1px 2px oklch(0.4 0.05 285 / 0.06);
}
.eb-seg button {
  border: 0;
  background: transparent;
  padding: 7px 14px;
  border-radius: 7px;
  font-family: ${SANS};
  font-size: 13px;
  font-weight: 500;
  color: ${C.muted};
  cursor: pointer;
  transition: background .15s ease, color .15s ease;
}
.eb-seg button:hover { background: oklch(0.96 0.01 285); }
.eb-seg button[data-on="true"] { background: ${C.violet}; color: ${C.white}; font-weight: 600; }

.eb-month .ant-select-selector {
  border-radius: 10px !important;
  border-color: ${C.line} !important;
  box-shadow: 0 1px 2px oklch(0.4 0.05 285 / 0.06) !important;
  font-weight: 600;
  font-size: 13px;
}
.eb-month:hover .ant-select-selector { border-color: oklch(0.78 0.06 285) !important; }

.eb-item + .eb-item { border-top: 1px solid ${C.lineSoft}; }
.eb-item:hover { background: ${C.wash}; }
`;

const Mono = ({ children, style }) => (
  <span style={{ fontFamily: MONO, fontWeight: 500, ...style }}>{children}</span>
);

const Dot = ({ color }) => (
  <span style={{ width: 8, height: 8, borderRadius: 999, background: color, flex: '0 0 auto' }} />
);

/**
 * The API describes each line in one long sentence. The sidebar wants a short
 * title over the arithmetic, so known line types get a label and the rest fall
 * back to the description the server sent.
 */
const ITEM_TITLES = {
  MAU_MAU_PAID: 'Paid Users',
  BANDWIDTH_FREEMIUM: 'Free tier',
  MAU_BANDWIDTH_FREEMIUM: 'Free tier bandwidth',
  MAU_MAU_TRIAL: 'Trial users',
  MINIMUM_COMMITMENT: 'Minimum commitment'
};

const itemTitle = (item) => ITEM_TITLES[item.type] || item.description;

const itemCalc = (item) => {
  if (item.type === 'MINIMUM_COMMITMENT') return 'Contract floor';
  const rate = formatUsd(item.rate);
  if (item.bandwidthGb > 0) return `${formatGb(item.bandwidthGb)} × ${rate}`;
  if (item.count > 0) return `${formatCount(item.count)} users × ${rate}`;
  return rate;
};

const EstimatedBilling = () => {
  const { keycloak } = useKeycloak();

  const [loading, setLoading] = useState(true);
  const [estimate, setEstimate] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [chartType, setChartType] = useState(CHART_TYPES.AREA);
  const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));

  const isCurrentMonth = moment(selectedMonth, 'YYYY-MM').isSame(moment(), 'month');
  const monthLabel = moment(selectedMonth, 'YYYY-MM').format('MMMM YYYY');

  const fetchBillingData = async (fromDate, toDate) => {
    try {
      setLoading(true);
      const token = keycloak?.token;
      if (!token) {
        message.error('Authentication token not found');
        return;
      }

      const response = await axios.get(
        `${BASE_URL}/api/billing/estimate-billing?sFromDate=${fromDate}&sToDate=${toDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response?.data?.header?.code !== 200) {
        setEstimate(null);
        setLoadError(response?.data?.header?.message || 'No billing data found for this month');
        return;
      }

      const normalized = normalizeEstimate(response.data.body);
      setEstimate(normalized);
      // The billing manager isolates per-reseller failures rather than failing
      // the request, so a 200 can still carry an error for this reseller.
      setLoadError(normalized ? normalized.error : 'No billing data found for this month');
    } catch (error) {
      setEstimate(null);
      setLoadError('Failed to fetch billing data');
      message.error('Failed to fetch billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    fetchBillingData(
      moment(value, 'YYYY-MM').startOf('month').format('YYYY-MM-DD'),
      moment(value, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')
    );
  };

  useEffect(() => {
    handleMonthChange(moment().format('YYYY-MM'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMonthOptions = () => {
    const options = [];
    for (let i = 0; i < 12; i++) {
      const monthDate = moment().subtract(i, 'months');
      options.push({
        value: monthDate.format('YYYY-MM'),
        label: monthDate.format('MMMM YYYY')
      });
    }
    return options;
  };

  const dailyStats = estimate?.dailyStats ?? [];

  const peak = dailyStats.reduce(
    (best, row) => (row.paid_users > best.value ? { value: row.paid_users, date: row.date } : best),
    { value: 0, date: null }
  );

  const CustomTooltip = ({ active, payload, label: pointLabel }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: C.white,
          border: `1px solid ${C.line}`,
          borderRadius: 10,
          padding: '9px 12px',
          boxShadow: '0 8px 20px oklch(0.4 0.05 285 / 0.14)'
        }}>
          {pointLabel ? (
            <div style={{ ...label, marginBottom: 6 }}>{moment(pointLabel).format('D MMM YYYY')}</div>
          ) : null}
          {payload.map((entry, index) => {
            const isBandwidth = String(entry.dataKey).includes('bandwidth');
            return (
              <div key={index}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: C.body }}>
                  <Dot color={entry.color} />
                  <span style={{ flex: 1 }}>{entry.name}</span>
                  <Mono style={{ color: C.ink25 }}>
                    {isBandwidth ? formatGb(entry.value) : formatCount(entry.value)}
                  </Mono>
                </div>
                {/* The chart plots total bandwidth; the split lives here so the
                    line stays readable but the paid/trial detail is not lost. */}
                {isBandwidth && entry.payload && (
                  <div style={{ paddingLeft: 16, marginTop: 2 }}>
                    {[
                      ['Paid', entry.payload.paid_bandwidth_gb],
                      ['Trial', entry.payload.trial_bandwidth_gb]
                    ].map(([name, value]) => (
                      <div key={name} style={{ display: 'flex', gap: 8, fontSize: 11.5, color: C.muted6 }}>
                        <span style={{ flex: 1 }}>{name}</span>
                        <Mono style={{ fontSize: 11.5 }}>{formatGb(value ?? 0)}</Mono>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = { data: dailyStats, margin: { top: 10, right: 8, left: 0, bottom: 4 } };
    const axis = {
      axisLine: false,
      tickLine: false,
      tick: { fill: C.muted6, fontSize: 12, fontFamily: MONO }
    };

    const xAxis = (
      <XAxis
        dataKey="date"
        {...axis}
        allowDecimals={false}
        interval={0}
        tickFormatter={(value, index) => {
          const day = Number(moment(value).format('D'));
          if (index === 0 || index === dailyStats.length - 1 || day % 5 === 0) return day;
          return '';
        }}
      />
    );
    // Users and bandwidth share a time axis but not a unit, so bandwidth is
    // plotted against its own axis on the right.
    const yAxis = <YAxis yAxisId="left" {...axis} width={44} domain={[0, 'dataMax']} />;
    const yAxisRight = (
      <YAxis
        yAxisId="right"
        orientation="right"
        {...axis}
        width={56}
        domain={[0, 'dataMax']}
        tickFormatter={(value) => formatGb(value)}
      />
    );
    const grid = <CartesianGrid vertical={false} stroke={C.grid} />;

    switch (chartType) {
      case CHART_TYPES.BAR:
        return (
          <BarChart {...commonProps}>
            {grid}{xAxis}{yAxis}{yAxisRight}
            <Tooltip cursor={{ fill: C.wash }} content={<CustomTooltip />} />
            <Bar yAxisId="left" dataKey="paid_users" name="Paid users" fill={C.violet} radius={[4, 4, 0, 0]} />
            <Bar yAxisId="left" dataKey="trial_users" name="Trial users" fill={C.blue} radius={[4, 4, 0, 0]} />
            <Bar yAxisId="right" dataKey="bandwidth_gb" name="Bandwidth" fill={C.teal} radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      case CHART_TYPES.LINE:
        return (
          <LineChart {...commonProps}>
            {grid}{xAxis}{yAxis}{yAxisRight}
            <Tooltip content={<CustomTooltip />} />
            <Line yAxisId="left" type="monotone" dataKey="paid_users" name="Paid users" stroke={C.violet} strokeWidth={3} dot={false} strokeLinecap="round" />
            <Line yAxisId="left" type="monotone" dataKey="trial_users" name="Trial users" stroke={C.blue} strokeWidth={3} dot={false} strokeLinecap="round" />
            <Line yAxisId="right" type="monotone" dataKey="bandwidth_gb" name="Bandwidth" stroke={C.teal} strokeWidth={2.5} strokeDasharray="5 4" dot={false} strokeLinecap="round" />
          </LineChart>
        );
      case CHART_TYPES.PIE: {
        const totalPaid = dailyStats.reduce((sum, item) => sum + item.paid_users, 0);
        const totalTrial = dailyStats.reduce((sum, item) => sum + item.trial_users, 0);
        return (
          <PieChart>
            <Pie
              data={[{ name: 'Paid users', value: totalPaid }, { name: 'Trial users', value: totalTrial }]}
              cx="50%" cy="50%" innerRadius={80} outerRadius={132} paddingAngle={2}
              dataKey="value" stroke="none"
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            >
              <Cell fill={C.violet} />
              <Cell fill={C.blue} />
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        );
      }
      case CHART_TYPES.AREA:
      default:
        // ComposedChart, not AreaChart: AreaChart declares Area as its only
        // graphical child, so a Line inside it renders nothing.
        return (
          <ComposedChart {...commonProps}>
            <defs>
              <linearGradient id="ebPaidFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.violet} stopOpacity={0.28} />
                <stop offset="100%" stopColor={C.violet} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="ebTrialFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.blue} stopOpacity={0.3} />
                <stop offset="100%" stopColor={C.blue} stopOpacity={0.03} />
              </linearGradient>
            </defs>
            {grid}{xAxis}{yAxis}{yAxisRight}
            <Tooltip content={<CustomTooltip />} />
            <Area yAxisId="left" type="monotone" dataKey="paid_users" name="Paid users" stroke={C.violet} strokeWidth={3} strokeLinecap="round" fill="url(#ebPaidFill)" />
            <Area yAxisId="left" type="monotone" dataKey="trial_users" name="Trial users" stroke={C.blue} strokeWidth={3} strokeLinecap="round" fill="url(#ebTrialFill)" />
            {/* Bandwidth rides on top as a dashed line — a third filled band
                would bury the user areas underneath it. */}
            <Line yAxisId="right" type="monotone" dataKey="bandwidth_gb" name="Bandwidth" stroke={C.teal} strokeWidth={2.5} strokeDasharray="5 4" dot={false} strokeLinecap="round" />
          </ComposedChart>
        );
    }
  };

  const renderStats = () => {
    const { bandwidth, actual } = estimate;
    const cap = bandwidth.caps[0];
    const split = bandwidth.paid + bandwidth.trial + bandwidth.freemium;
    const pct = (part) => (split > 0 ? `${(part / split) * 100}%` : '0%');
    // While the window is open the headline figures are projected to month end.
    // The actuals sit beside them so the two never read as contradicting.
    const isProjected = !estimate.isComplete;
    // The day count already sits in the page header, so the per-card captions
    // stay short.
    const toDate = 'to date';

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'stretch' }}>
        {/* Estimated invoice — the headline figure */}
        <div style={{
          background: 'linear-gradient(160deg, oklch(0.42 0.17 285), oklch(0.32 0.13 288))',
          borderRadius: 14,
          padding: '14px 16px',
          color: 'oklch(0.98 0.01 285)',
          boxShadow: '0 8px 18px oklch(0.4 0.12 285 / 0.18)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          flex: '1 1 210px',
          minWidth: 210
        }}>
          <div style={{ ...label, color: 'inherit', opacity: 0.72 }}>
            {isCurrentMonth ? 'Estimated invoice' : 'Billed amount'}
          </div>
          <div style={{ ...figure, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            {formatUsd(estimate.invoiceAmount)}
            {isProjected && (
              <span style={{ fontSize: 11.5, fontWeight: 500, opacity: 0.72, letterSpacing: 0 }}>projected</span>
            )}
          </div>
          {/* Priced off the actual counts, not the headline scaled down — a
              smaller count can sit in a different slab. */}
          {isProjected && (
            <div style={{ fontSize: 11.5, opacity: 0.78 }}>
              <Mono style={{ fontSize: 12, color: 'inherit' }}>{formatUsd(actual.invoiceAmount)}</Mono> to date
            </div>
          )}
          {/* Cards stretch to the tallest in the row; the meta line sits on the
              floor so the extra height reads as spacing, not as a gap. */}
          <div style={{ fontSize: 11.5, opacity: 0.75, marginTop: 'auto' }}>
            {estimate.isComplete
              ? `${estimate.billingMonth} · final`
              : estimate.billingMonth}
          </div>
        </div>

        {estimate.hasMaa && estimate.maa !== null && (
          <div style={{ ...card, padding: '14px 16px', gap: 6, flex: '1 1 210px', minWidth: 210 }}>
            <div style={label}>MAA</div>
            <div style={figure}>{formatCount(estimate.maa)}</div>
            {isProjected && actual.maa !== null && (
              <div style={{ fontSize: 11.5, color: C.muted6, marginTop: 'auto' }}>
                <Mono style={{ fontSize: 11.5, color: C.ink25 }}>{formatCount(actual.maa)}</Mono> {toDate}
              </div>
            )}
          </div>
        )}

        {estimate.hasMau && (
          <div style={{ ...card, padding: '14px 16px', gap: 6, flex: '1 1 210px', minWidth: 210 }}>
            <div style={label}>
              {isCurrentMonth ? 'Users this month' : `Users in ${moment(selectedMonth, 'YYYY-MM').format('MMMM')}`}
            </div>
            <div style={{ ...figure, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              {formatCount(estimate.connectedMau)}
              {isProjected && (
                <span style={{ fontSize: 11.5, fontWeight: 500, color: C.muted6, letterSpacing: 0 }}>projected</span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', fontSize: 12, marginTop: 'auto' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: C.muted }}>
                <Dot color={C.violet} />Paid <Mono style={{ color: C.ink25 }}>{formatCount(estimate.paidMau)}</Mono>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: C.muted }}>
                <Dot color={C.blue} />Trial <Mono style={{ color: C.ink25 }}>{formatCount(estimate.trialMau)}</Mono>
              </span>
            </div>
            {/* The figures above are all projected; this is the only actual. */}
            {isProjected && (
              <div style={{ fontSize: 11.5, color: C.muted6 }}>
                <Mono style={{ fontSize: 11.5, color: C.ink25 }}>{formatCount(actual.connectedMau)}</Mono> {toDate}
              </div>
            )}
          </div>
        )}

        {estimate.hasBandwidth && (
          <div style={{ ...card, padding: '14px 16px', flex: '2 1 320px', minWidth: 300 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: '4px 12px' }}>
              <div style={label}>Bandwidth</div>
              {cap && (
                <div style={{ fontSize: 12, color: C.muted6, whiteSpace: 'nowrap' }}>cap {cap.capGb} GB</div>
              )}
            </div>
            <div style={{ ...figure, marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              {formatGb(bandwidth.total)}
              {isProjected && (
                <span style={{ fontSize: 11.5, fontWeight: 500, color: C.muted6, letterSpacing: 0 }}>projected</span>
              )}
            </div>

            {/* Paid / trial / freemium as one continuous bar. */}
            <div style={{
              display: 'flex', height: 6, borderRadius: 999, overflow: 'hidden',
              marginTop: 10, background: 'oklch(0.94 0.01 285)', gap: 2
            }}>
              <div style={{ width: pct(bandwidth.paid), background: C.violet }} />
              <div style={{ width: pct(bandwidth.trial), background: C.blue }} />
              <div style={{ width: pct(bandwidth.freemium), background: 'oklch(0.82 0.08 285)' }} />
            </div>

            {cap && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 7, fontSize: 11, color: 'oklch(0.58 0.02 285)' }}>
                <span>{formatCount(cap.aboveCapMau)} users at or above cap</span>
                <span>{formatCount(cap.belowCapMau)} below</span>
              </div>
            )}

            {/* The split below is measured, not projected — it belongs to the
                actual total, so that total heads the group. */}
            {isProjected && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 12, marginTop: 12, fontSize: 11.5, color: C.muted6
              }}>
                <span>{toDate}</span>
                <Mono style={{ fontSize: 12.5, color: C.ink25 }}>{formatGb(actual.bandwidthTotal)}</Mono>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(84px, 1fr))', gap: 8, marginTop: isProjected ? 6 : 10 }}>
              {[
                { label: 'Paid', value: formatGb(bandwidth.paid) },
                { label: 'Trial', value: formatGb(bandwidth.trial) },
                { label: 'Freemium', value: formatGb(bandwidth.freemium) }
              ].map((row) => (
                <div key={row.label} style={{ padding: '7px 10px', borderRadius: 9, background: C.wash }}>
                  <div style={{ fontSize: 11, color: C.muted }}>{row.label}</div>
                  <Mono style={{ fontSize: 12.5, marginTop: 2, display: 'block' }}>{row.value}</Mono>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBreakdown = () => {
    const isProjected = !estimate.isComplete;
    // Lines priced off the actual counts, looked up per row so a projected line
    // that has not earned anything yet still reads $0.00 rather than blank.
    const actualByLine = new Map(estimate.actual.items.map((item) => [item.matchKey, item]));

    return (
    <div className="eb-aside" style={{ ...card, padding: '20px 20px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>Invoice breakdown</div>
        <div style={{
          fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
          background: C.chip, color: C.violetDeep, whiteSpace: 'nowrap'
        }}>
          {estimate.isComplete ? 'Final' : 'Projected'}
        </div>
      </div>
      <div style={{ fontSize: 12.5, color: C.muted57, marginTop: 4 }}>
        {estimate.billingMonth} · {estimate.items.length} line item{estimate.items.length === 1 ? '' : 's'}
        {!estimate.isComplete && ` · ${estimate.elapsedDays} of ${estimate.windowDays} days so far`}
      </div>

      {estimate.minimumCommitmentApplied && (
        <div style={{
          marginTop: 12, padding: '10px 12px', borderRadius: 10,
          background: C.wash, color: C.body, fontSize: 12, lineHeight: 1.55
        }}>
          Usage of {formatUsd(estimate.usageAmount)} is below your minimum commitment of{' '}
          {formatUsd(estimate.minimumCommitment)}, so the commitment is billed instead.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', marginTop: 14 }}>
        {estimate.items.map((item) => (
          <div
            key={item.key}
            className="eb-item"
            style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '13px 0' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{itemTitle(item)}</span>
              <Mono style={{ fontSize: 11.5, color: 'oklch(0.56 0.02 285)' }}>{itemCalc(item)}</Mono>
              {/* The commitment line carries no quantity to compare against. */}
              {isProjected && item.type !== 'MINIMUM_COMMITMENT' && (
                <Mono style={{ fontSize: 11.5, color: C.muted6 }}>
                  {itemCalc(actualByLine.get(item.matchKey) ?? { ...item, count: 0, bandwidthGb: 0 })} to date
                </Mono>
              )}
            </div>
            {/* A commitment floor zeroes the usage lines but keeps them listed. */}
            {item.usageAmount !== null && item.usageAmount !== item.amount ? (
              <span style={{ whiteSpace: 'nowrap' }}>
                <Mono style={{ fontSize: 12, color: C.muted6, textDecoration: 'line-through', marginRight: 6 }}>
                  {formatUsd(item.usageAmount)}
                </Mono>
                <Mono style={{ fontSize: 13.5 }}>{formatUsd(item.amount)}</Mono>
              </span>
            ) : (
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                <Mono style={{ fontSize: 13.5, whiteSpace: 'nowrap' }}>{formatUsd(item.amount)}</Mono>
                {isProjected && (
                  <Mono style={{ fontSize: 11.5, color: C.muted6, whiteSpace: 'nowrap' }}>
                    {formatUsd(actualByLine.get(item.matchKey)?.amount ?? 0)}
                  </Mono>
                )}
              </span>
            )}
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 'auto', paddingTop: 16, display: 'flex', alignItems: 'flex-end',
        justifyContent: 'space-between', gap: 12, borderTop: `2px solid ${C.lineStrong}`
      }}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: C.body }}>
            {estimate.isComplete ? 'Total' : 'Projected total'}
          </span>
          {isProjected && (
            <span style={{ fontSize: 11.5, color: C.muted6 }}>
              To date
            </span>
          )}
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
          <Mono style={{ fontSize: 19, letterSpacing: '-0.02em' }}>{formatUsd(estimate.invoiceAmount)}</Mono>
          {isProjected && (
            <Mono style={{ fontSize: 12, color: C.muted6 }}>{formatUsd(estimate.actual.invoiceAmount)}</Mono>
          )}
        </span>
      </div>
    </div>
    );
  };

  const hasData = estimate && !estimate.error;

  return (
    <div className="eb-root col-design3">
      <style>{SCOPED_CSS}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.violet }}>
              Billing &amp; usage
            </div>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em' }}>
              {isCurrentMonth ? 'Current month estimated billing' : `${monthLabel} billing`}
            </h1>
            <div style={{ fontSize: 14, color: 'oklch(0.52 0.02 285)' }}>
              {monthLabel}
              {hasData && (estimate.isComplete
                ? ' · billing period closed'
                : ` · ${estimate.elapsedDays} of ${estimate.windowDays} days so far — the rest is estimated`)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="eb-seg">
              {chartOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  data-on={chartType === option.value}
                  onClick={() => setChartType(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Select
              className="eb-month"
              style={{ width: 170 }}
              value={selectedMonth}
              onChange={handleMonthChange}
              options={getMonthOptions()}
              dropdownStyle={{ maxHeight: '400px', overflowY: 'auto' }}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 360 }}>
            <Spin size="large" />
          </div>
        ) : !hasData ? (
          <div style={{ ...card, padding: '56px 20px', alignItems: 'center' }}>
            {/* A reseller-level error still returns a well-formed block of zeroes —
                rendering it as $0.00 would read as "you owe nothing". */}
            <Empty description={estimate?.error || loadError || 'No billing data available'} />
          </div>
        ) : (
          <div className="eb-grid">
            {renderStats()}

            <div style={{ ...card, padding: '22px 26px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>Daily active users</div>
                  {peak.date && (
                    <div style={{ fontSize: 12.5, color: C.muted57 }}>
                      Peak {formatCount(peak.value)} on {moment(peak.date).format('MMM D')}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12.5, color: C.body }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><Dot color={C.violet} />Paid users</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><Dot color={C.blue} />Trial users</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><Dot color={C.teal} />Bandwidth</span>
                </div>
              </div>

              {dailyStats.length > 0 ? (
                <>
                  <div style={{ marginTop: 6 }}>
                    <ResponsiveContainer width="100%" height={392}>
                      {renderChart()}
                    </ResponsiveContainer>
                  </div>
                  <div style={{
                    textAlign: 'center', fontSize: 12, fontWeight: 600, letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: 'oklch(0.62 0.02 285)', marginTop: 2
                  }}>
                    {monthLabel}
                  </div>
                </>
              ) : (
                <div style={{ height: 392, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted6, fontSize: 13 }}>
                  No chart data available
                </div>
              )}
            </div>

            {renderBreakdown()}
          </div>
        )}
      </div>
    </div>
  );
};

export default EstimatedBilling;
